import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AccessRoleTypes } from '@next/core-lib';
import { AgrEnvironment, EnvironmentInjector } from '@next/core-lib/environment';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { filter, switchMap, take } from 'rxjs';

import { AssociatedChainPartnerStore } from '../store/associated-chain-partner.store';
import { BYPASS_CHAIN_PARTY_INTERCEPTOR } from './chain-party-interceptor.context';

const withChainPartyHeader = (request: Parameters<HttpInterceptorFn>[0], chainPartyId: number) =>
  request.clone({
    setHeaders: { 'Next-ChainParty': `${chainPartyId}` },
  });

export const chainPartyInterceptor: HttpInterceptorFn = (request, next) => {
  if (request.context.get(BYPASS_CHAIN_PARTY_INTERCEPTOR)) {
    return next(request);
  }

  const store = inject(AssociatedChainPartnerStore);
  const appStore = inject(Store);
  const env: AgrEnvironment = inject(EnvironmentInjector);

  const chainPartyId = store.associatedChainPartnerId();
  const applications = appStore.selectSignal(UserSelectors.selectUserApplications)();

  const isIntermediary = applications.some(
    (application) =>
      application.ApplicationKey === env.APPLICATION_KEY &&
      application.RoleKey === AccessRoleTypes.Intermediary,
  );

  if (!isIntermediary) {
    return next(request);
  }

  if (chainPartyId) {
    return next(withChainPartyHeader(request, chainPartyId));
  }

  if (store.loadStatus() === 'loaded' || store.loadStatus() === 'error') {
    return next(request);
  }

  return toObservable(store.loadStatus).pipe(
    filter((loadStatus) => loadStatus === 'loaded' || loadStatus === 'error'),
    take(1),
    switchMap(() => {
      const loadedChainPartyId = store.associatedChainPartnerId();
      return next(loadedChainPartyId ? withChainPartyHeader(request, loadedChainPartyId) : request);
    }),
  );
};
